import { SerializedStyles, css } from "@emotion/react";
import React, { ReactNode } from "react";
import { pxToRem, weights, transition, mediaQueries, rgbaFromHex } from "../utils/css-utils";
import { colors } from "../utils/colors";
import { Link } from "gatsby";

type ButtonProps = {
  text?: string,
  href: string,
  level?: string,
  children?: ReactNode,
  css?: SerializedStyles
};

const Button: React.FC<ButtonProps> = ({ text, href, level, children, ...props }) => {
  const levelUsed = level ? level : 'primary';
  const isExternalLink = href.substring(0,4) === 'http';
  
  const baseBtnStyles = css`
    position: relative;
    display: inline-flex;
    justify-content: center;
    padding: ${pxToRem(10)} ${pxToRem(18)};
    border: ${pxToRem(2)} solid transparent;
    border-radius: ${pxToRem(32)};
    font-family: "Space Grotesk", sans-serif;
    font-size: ${pxToRem(16)};
    line-height: 1;
    text-align: center;
    text-decoration: none;
    font-weight: ${weights.bold};
    transition: ${transition()};
    white-space: nowrap;
    
    &::before {
      content: '';
      position: absolute;
      top: ${pxToRem(-4)};
      right: ${pxToRem(-4)};
      bottom: ${pxToRem(-4)};
      left: ${pxToRem(-4)};
      border-radius: inherit;
      box-shadow: none;
      transition: ${transition()};
    }
    
    ${mediaQueries.md} {
      padding: ${pxToRem(18)} ${pxToRem(22)};
    }
    
    ${mediaQueries.xl} {
      padding: ${pxToRem(17)} ${pxToRem(30)};
      border-radius: ${pxToRem(40)};
      font-size: ${pxToRem(18)};
    }
    
    &:focus-visible {
      outline: none;
      
      &::before {
        box-shadow: 0 0 ${pxToRem(2)} ${pxToRem(2)} ${colors.yellow600};
      }
    }
  `;
  
  const primaryStyles = css`
    border-color: ${colors.violet600};
    background: ${colors.violet600};
    color: ${colors.violet100};
    
    &:hover {
      background: ${colors.violet500};
      border-color: ${colors.violet500};
      color: ${colors.white};
    }
    
    &:active {
      background: ${colors.violet400};
      border-color: ${colors.violet400};
      color: ${colors.violet900};
    }
  `;
  
  const secondaryStyles = css`
    border-color: ${colors.white};
    background: transparent;
    color: ${colors.white};
    
    &:hover,
    &:active {
      background: ${colors.white};
      border-color: ${colors.white};
      color: ${colors.black900};
    }
    
    &:focus-visible {
      border-color: ${rgbaFromHex(colors.neutral400, 30)};
    }
  `;
  
  const combinedBtnStyles = [baseBtnStyles];
  
  switch (levelUsed) {
    case 'secondary':
      combinedBtnStyles.push(secondaryStyles);
      break;
    case 'primary':
    default:
      combinedBtnStyles.push(primaryStyles);
  }
  
  if (isExternalLink) {
    return (
      <a {...props} href={href} css={combinedBtnStyles}>{children || text}</a>
    )
  }
  else {
    return (
      <Link {...props} to={href} css={combinedBtnStyles}>{children || text}</Link>
    )
  }
};

export default Button;
